import * as React from "react"
import { FC, useEffect, useState } from "react"
import {
  AnalysisSummaries,
  AnalysisSummary,
  CurrencyExchange,
  ReportsApprovePaymentRequest,
  SortParams,
  User,
  UserBalanceTotal
} from "../../../../types"
import { connect } from "react-redux"
import { RootState } from "../../../../store"
import {
  ADMIN_PERMISSIONS,
  ANALYSIS_FILTER,
  analysisFilterOptions,
  currencyOptions,
  errorFilledToastOptions,
  successFilledToastOptions,
  USER_BALANCE_EXPORT_HEADERS,
  USER_ROLE
} from "../../../../constants"
import {
  reportsApprovalAllAccountColumns,
  reportsApprovalAllPlatformTotalColumns,
  reportsApprovalPlatformColumns,
  reportsApprovalStoreFrontColumns,
  reportsApprovalTrackColumns,
  reportsUserBalanceColumns
} from "./columns/reportsApprovalColumns"
import {
  approveSummary,
  transferSummary,
  transferBackSummary,
  fetchApprovalSummaries,
  updateApprovalSummaries,
  paymentMethodsAdminEntry,
  selectUser,
  disapproveSummary
} from "../../../../actions/adminActions"
import {
  currencyExchange,
  handleAuthorizedRequest,
  kahunaPaymentsReceive,
  releases,
  summaries as summariesApi,
  userPayments,
  users
} from "../../../../apis"
import ApproveModal from "./modal/ApproveModal"
import { toast } from "react-toastify"
import {
  checkAdminPermission,
  createDateObject,
  formatDateObject,
  getCurrency,
  getFilterString,
  getMonthRange,
  getPaymentPeriodOptions,
  mapListToCamelCase,
  mapToSnakeCase,
  yyyymmToDate
} from "../../../../utility"
import { sum } from "lodash"
import ApproveAllModal from "./modal/ApproveAllModal"
import ApprovePaymentModal from "./modal/ApprovePaymentModal"
import CurrencyExchangeModal from "./modal/CurrenyExchangeModal"
import {
  KButton,
  KSpan,
  KDropdown,
  KTitleSpan,
  KTooltip,
  KSelectDate,
  KInput,
  KSelectRange
} from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import TableNew from "../../../ui/table/TableNew"
import PaymentBatches from "./PaymentBatches"
//@ts-ignore
import CalendarIcon from "../../../../assets/admin_icons/calendar-dark.svg"
//@ts-ignore
import PresentationIcon from "../../../../assets/admin_icons/presentation-chart.svg"
//@ts-ignore
import WalletIcon from "../../../../assets/admin_icons/wallet.svg"
//@ts-ignore
import MoneyIcon from "../../../../assets/admin_icons/money-cash.svg"
import AdminPaymentMethodModal from "../user/modals/AdminPaymentMethodModal"
//@ts-ignore
import DownloadIcon from "../../../../assets/admin_icons/download-white.svg"
import { RefreshOutline } from "heroicons-react"
import SearchUserComponent from "../../../ui/components/SearchUserComponent"

type ReportsApprovalProps = {
  user: User
  summaries: AnalysisSummaries
  selectUser: (user: User) => void
  paymentMethodsAdminEntry: (adminPanelUserId?: number) => void
  fetchApprovalSummaries: (
    type: string,
    limit: number,
    offset: number,
    filters?: { [key: string]: string | number | boolean },
    sortField?: string,
    sortDirection?: "asc" | "desc"
  ) => Promise<void>
  updateApprovalSummaries: (summaries: AnalysisSummaries) => void
  approveSummary: (summary: AnalysisSummary, type: string) => Promise<void>
  disapproveSummary: (summary: AnalysisSummary) => Promise<void>
  transferSummary: (summary: AnalysisSummary, transferDate: string) => Promise<void>
  transferBackSummary: (summary: AnalysisSummary) => Promise<void>
}

const paymentPeriodOptions = [
  { label: "Quarterly View", value: 1 },
  { label: "Monthly View", value: 2 },
  { label: "Payment View", value: 999 }
]

const dateOptions = [
  { label: "Activity Period", value: 0 },
  { label: "Reporting Period", value: 2 },
  { label: "Payment Period", value: 3 }
]

const paymentPeriodFilterOptions = [
  { label: "Filter Quarterly ", value: 1 },
  { label: "Filter Monthly", value: 2 }
]

const companyOptions = [
  { label: "TR ", value: 1, value2: "TR" },
  { label: "GB ", value: 2, value2: "GB" }
]

const paymentStatusFilterOptions = [
  { label: "Paid", value: 1, value2: "True" },
  { label: "Unpaid", value: 2, value2: "False" }
]

const thresholdVisibilityOptions = [
  { label: "Show All", value: 1 },
  { label: "Threshold -", value: 2 },
  { label: "Threshold +", value: 3 }
]

enum PaymentPeriods {
  Quarterly = 1,
  Monthly = 2,
  Payment = 999
}

const ReportsApproval: FC<ReportsApprovalProps> = (props) => {
  const {
    approveSummary,
    disapproveSummary,
    transferSummary,
    transferBackSummary,
    fetchApprovalSummaries,
    updateApprovalSummaries,
    summaries
  } = props

  const [loading, setLoading] = useState(true)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [timeoutId, setTimeoutId] = useState(null)
  const [periods, setPeriods] = useState<KSelectOption[]>([])
  const [quarters, setQuarters] = useState<KSelectOption[]>([])
  const [selectedPeriod, setSelectedPeriod] = useState<string>("")
  const [selectedCompany, setSelectedCompany] = useState<KSelectOption>()
  const [selectedPeriodList, setSelectedPeriodList] = useState<string[]>([])
  const [selectedQuarter, setSelectedQuarter] = useState<KSelectOption>()
  const [selectedPeriodDate, setSelectedPeriodDate] = useState<Date | undefined>(undefined)
  const [data, setData] = useState<AnalysisSummary[]>([])
  const [modalOpen, setModalOpen] = useState(false)
  const [approveAllModalOpen, setApproveAllModalOpen] = useState(false)
  const [approvePaymentModalOpen, setApprovePaymentModalOpen] = useState(false)
  const [userPaymentPeriodId, setUserPaymentPeriodId] = useState<number | undefined>()
  const [editedSummary, setEditedSummary] = useState<AnalysisSummary>()
  const [currencyRates, setCurrencyRates] = useState<CurrencyExchange[]>([])
  const [selectedUser, setSelectedUser] = useState<KSelectOption | undefined>()
  const [selectedPaymentUser, setSelectedPaymentUser] = useState<KSelectOption | undefined>()
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(10)
  const [currencyOfferModal, setCurrencyOfferModal] = useState(false)
  const [selectedCurrency, setSelectedCurrency] = useState<string>("GBP")
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [selectedPaymentPeriod, setSelectedPaymentPeriod] = useState<KSelectOption>({
    label: "Payment View",
    value: 999
  })
  const [selectedDateOption, setSelectedDateOption] = useState<KSelectOption>(dateOptions[0])
  const [selectedPaymentPeriodFilter, setSelectedPaymentPeriodFilter] = useState<KSelectOption>()
  const [selectedPaymentStatusFilter, setSelectedPaymentStatusFilter] = useState<KSelectOption>()
  const [selectedThresholdOption, setSelectedThresholdOption] = useState<KSelectOption>(thresholdVisibilityOptions[0])
  const [openSidebar, setOpenSidebar] = useState<boolean>(true)
  const [selectedFilter, setSelectedFilter] = useState<string>(
    props.user?.roleId === USER_ROLE.AccountingAdmin ? ANALYSIS_FILTER.USER_BALANCE : ANALYSIS_FILTER.TOP_PLATFORMS
  )
  const [checkedThreshold, setCheckedThreshold] = useState(false)
  const [selectedPaymentMethodUserId, setSelectedPaymentMethodUserId] = useState<number>()

  const [fetchedSummaries, setFetchedSummaries] = useState<AnalysisSummaries>(summaries)
  const [filters, setFilters] = useState<{ [key: string]: string | number | boolean }>()
  const [sortField, setSortField] = useState<string>()
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | undefined>()
  const [totals, setTotals] = useState<UserBalanceTotal[]>([])
  const [kahunaPaymentOpen, setKahunaPaymentOpen] = useState<boolean>(false)
  const [approved, setApproved] = useState(true)

  useEffect(() => {
    setFetchedSummaries(summaries)
  }, [summaries])

  const isTileDisabled = ({ date }: { date: Date }) => {
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, "0")
    const formattedDate = `${year}${month}` // Format as YYYYMM
    const includedPeriods = periods.map((period) => String(period.value))

    if (selectedFilter === ANALYSIS_FILTER.USER_BALANCE && selectedPaymentPeriod.value === PaymentPeriods.Payment) {
      const latestPeriod = includedPeriods.sort().slice(-1)[0]

      if (latestPeriod) {
        const latestYear = Number(latestPeriod.slice(0, 4))
        const latestMonth = Number(latestPeriod.slice(4, 6))

        const latestDate = new Date(latestYear, latestMonth - 1)

        for (let i = 1; i <= 3; i++) {
          const nextDate = new Date(latestDate)
          nextDate.setMonth(nextDate.getMonth() + i)

          includedPeriods.push(`${nextDate.getFullYear()}${String(nextDate.getMonth() + 1).padStart(2, "0")}`)
        }
      }
    }

    return !includedPeriods.includes(formattedDate)
  }

  const [columns, setColumns] = useState(
    reportsApprovalPlatformColumns(
      selectedIds,
      setSelectedIds,
      [...(fetchedSummaries[selectedFilter] || [])].map((data) => data.id),
      selectedUser?.value !== undefined
    )
  )

  useEffect(() => {
    const getSummaryPeriods = async () => {
      await handleAuthorizedRequest(
        async () => {
          const response = await summariesApi("/summary_periods", "get")
          if (response.status === 200) {
            const sortedList = response.data.sort().reverse()
            setPeriods(sortedList.map((period) => ({ label: period, value: period })))
            setSelectedPeriod(sortedList[0])
            setSelectedPeriodList([sortedList[0]])
            const date = createDateObject(sortedList[0])
            setSelectedPeriodDate(date)
            const paymentPeriodTypeOptions = getPaymentPeriodOptions(PaymentPeriods.Quarterly, sortedList[0])
            setQuarters(paymentPeriodTypeOptions)
            setSelectedQuarter(paymentPeriodTypeOptions[paymentPeriodTypeOptions.length - 1])
          }
        },
        "Error on fetch summery periods.",
        undefined
      )
    }
    getSummaryPeriods().then()
  }, [])

  const onSelectPaymentMethodUserId = (selectedPaymentMethodUserId?: number, username?: string) => {
    setSelectedPaymentMethodUserId(selectedPaymentMethodUserId)
    props.paymentMethodsAdminEntry(selectedPaymentMethodUserId)
    //@ts-ignore
    props.selectUser({ id: selectedPaymentMethodUserId, firstName: username, lastName: "" })
  }

  const onApprovePayment = (userId?: number, username?: string, paymentPeroidId?: number) => {
    if (!userId || !paymentPeroidId) {
      return
    }
    setUserPaymentPeriodId(paymentPeroidId)
    setSelectedPaymentUser({ label: username || "-", value: userId })
    setTimeout(() => {
      setApprovePaymentModalOpen(true)
    }, 200)
  }

  const onClickKahunaPayment: (row: AnalysisSummary) => void = (row) => {
    if (!row.date || !row.label) {
      toast.error("Missing date or distributor information for Kahuna payment.", errorFilledToastOptions)
      return
    }

    const amount = window.prompt(
      `Update kahuna recieved payment for:\n\n- Date: ${row.date}\n- Distributor: ${row.label}\n- Currency: ${selectedCurrency}\n\nPlease enter the amount received from distributor:`
    )

    if (!amount) {
      toast.error("Missing amount.", errorFilledToastOptions)
      return
    }

    kahunaPaymentsReceive(`/save/`, "POST", {
      date: row.date,
      distributor: row.label,
      received_amount: amount,
      currency: selectedCurrency
    }).then((resp) => {
      if (!resp.data.success) {
        toast.error("Error updating Kahuna payment.", errorFilledToastOptions)
      }

      toast.success("Kahuna payment updated successfully.", successFilledToastOptions)
      const allPlatformTotalsUpdated: AnalysisSummary[] = fetchedSummaries.allPlatformTotal.map((summary) => {
        const monthlyList =
          summary.monthlyList?.map((monthly) => {
            if (monthly.date === row.date && monthly.label === row.label) {
              return { ...monthly, kahunaReceivedAmount: amount }
            }
            return monthly
          }) || []
        summary.monthlyList = monthlyList
        if (summary.date === row.date && summary.label === row.label) {
          return { ...summary, kahunaReceivedAmount: amount }
        }
        return summary
      })
      updateApprovalSummaries({ ...fetchedSummaries, allPlatformTotal: [...allPlatformTotalsUpdated] })
    })
  }

  const handleColumnChange = () => {
    if (selectedFilter === ANALYSIS_FILTER.PAYMENT_BATCHES) return
    const dataIds = fetchedSummaries[selectedFilter].map((data) => data.id)
    if (selectedFilter === ANALYSIS_FILTER.TOP_PLATFORMS) {
      setColumns(
        reportsApprovalPlatformColumns(selectedIds, setSelectedIds, dataIds, selectedUser?.value !== undefined)
      )
    } else if (selectedFilter === ANALYSIS_FILTER.TOP_TRACKS) {
      setColumns(reportsApprovalTrackColumns())
    } else if (selectedFilter === ANALYSIS_FILTER.TOP_COUNTRIES) {
      setColumns(reportsApprovalStoreFrontColumns())
    } else if (selectedFilter === ANALYSIS_FILTER.ALL_PLATFORM_TOTAL) {
      setColumns(reportsApprovalAllPlatformTotalColumns(kahunaPaymentOpen, onClickKahunaPayment))
    } else if (selectedFilter === ANALYSIS_FILTER.ALL_ACCOUNTS) {
      setColumns(reportsApprovalAllAccountColumns())
    } else if (selectedFilter === ANALYSIS_FILTER.USER_BALANCE) {
      const isQuarterly = selectedPaymentPeriod.value === PaymentPeriods.Quarterly
      setColumns(
        reportsUserBalanceColumns(
          isQuarterly,
          selectedPeriodList,
          onSelectPaymentMethodUserId,
          onApprovePayment,
          props.user?.adminPermissions
        )
      )
    }
  }
  useEffect(() => {
    handleColumnChange()
  }, [selectedIds])

  useEffect(() => {
    if (selectedFilter === ANALYSIS_FILTER.PAYMENT_BATCHES) return
    handleColumnChange()
    setData(fetchedSummaries[selectedFilter])
    setUserPaymentPeriodId(undefined)
    if (selectedUser?.value) {
      ;[ANALYSIS_FILTER.TOP_COUNTRIES, ANALYSIS_FILTER.TOP_PLATFORMS, ANALYSIS_FILTER.TOP_TRACKS].forEach((filter) => {
        if (fetchedSummaries[filter].length > 0 && fetchedSummaries[filter][0].paymentPeriodId) {
          setUserPaymentPeriodId(fetchedSummaries[filter][0].paymentPeriodId)
        }
      })
    }
  }, [fetchedSummaries])

  useEffect(() => {
    if (selectedThresholdOption.value === 1) {
    }
  }, [selectedThresholdOption])

  useEffect(() => {
    const getCurrencies = async () => {
      const currencyResponse = await currencyExchange(`/?date=${selectedPeriod}`, "get")
      if (currencyResponse.status === 200) {
        setCurrencyRates(mapListToCamelCase(currencyResponse.data.results))
      } else {
        toast.error("Error while fetching currencies.", errorFilledToastOptions)
      }
    }
    if (selectedPeriod) {
      getCurrencies().then()
    }
  }, [selectedPeriod])

  useEffect(() => {
    setOffset(0)
    setSelectedIds([])
  }, [selectedPeriod, selectedFilter, selectedUser])

  const onSortOrFilterChange = async (
    filters: { [key: string]: string | number | boolean },
    sortField: string,
    sortDirection?: "asc" | "desc",
    debounce?: boolean
  ) => {
    setFilters(filters)
    setSortField(sortField)
    setSortDirection(sortDirection)
    if (!selectedUser) {
      delete filters["user__username"]
    }

    setLoading(true)
    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    const limit = selectedUser?.value || selectedFilter === ANALYSIS_FILTER.ALL_PLATFORM_TOTAL ? 100 : 10
    setLimit(limit)

    setTimeoutId(
      //@ts-ignore
      setTimeout(
        () => {
          fetchApprovalSummaries(selectedFilter, limit, offset, filters, sortField, sortDirection).then(() => {
            setTotals([])
            setLoading(false)
          })
        },
        debounce ? 500 : 0
      )
    )
  }

  const onClickPage = (pageNumber: number) => {
    setOffset((pageNumber - 1) * limit)
  }

  const onSelectRow = (index) => {
    if (!checkAdminPermission(ADMIN_PERMISSIONS.REPORTS_APPROVAL_REPORT_APPROVE, props.user?.adminPermissions)) {
      return
    }

    if (
      index !== null &&
      index >= 0 &&
      selectedFilter === ANALYSIS_FILTER.TOP_PLATFORMS &&
      data[index].paymentApproved === false
    ) {
      setEditedSummary(data[index])
      setModalOpen(true)
    }
  }

  const approveReportSummary = async (values: AnalysisSummary) => {
    await approveSummary({ ...values, totalNetAll: values.totalNet }, selectedFilter)
    setModalOpen(false)
  }

  const disapproveReportSummary = async (values: AnalysisSummary) => {
    await disapproveSummary({ ...values })
    setModalOpen(false)
  }

  const transferReportSummary = async (values: AnalysisSummary, transferDate: string) => {
    await transferSummary({ ...values }, transferDate)
    setModalOpen(false)
  }

  const transferBackReportSummary = async (values: AnalysisSummary) => {
    await transferBackSummary({ ...values })
    setModalOpen(false)
  }

  const downloadLink = (downloadData: string[][]) => {
    const csvContent = downloadData.map((row) => row.join(",")).join("\n")
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })

    const link = document.createElement("a")
    const url = URL.createObjectURL(blob)

    link.setAttribute("href", url)
    link.setAttribute("download", `User_Balance_${selectedPeriod}_${selectedPaymentPeriod.label}`)
    link.style.visibility = "hidden"

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const downloadUserBalance = (userBalanceList: AnalysisSummary[]) => {
    const dataToDownload: string[][] = [USER_BALANCE_EXPORT_HEADERS]

    mapListToCamelCase(userBalanceList).forEach((userBalance: AnalysisSummary) => {
      const rowList = [userBalance]
      if (selectedPaymentPeriod.value === PaymentPeriods.Quarterly) {
        userBalance.monthlyList?.forEach((subRow: AnalysisSummary) => {
          rowList.push({
            ...subRow,
            userName: "",
            user: "",
            taxTypeName: "",
            userCompany: "",
            paymentPeriodName: subRow.date
          })
        })
      }

      rowList.forEach((row) => {
        const revenueRoyalty = `${Number(row.totalNetAll || 0) - row.affiliateTotal} ${getCurrency(
          row.totalNetCurrency
        )}`
        const revenueAffiliate = `${Number(row.affiliateTotal).toFixed(2)} ${getCurrency(row.totalNetCurrency)}`
        const revenueTotal = `${Number(row.totalNetAll).toFixed(2)} ${getCurrency(row.totalNetCurrency)}`

        const totalApprovedAdvance = row?.totalApprovedDetails?.advance || 0
        const advanceCurrency = totalApprovedAdvance > 0 ? getCurrency(row.advanceCurrency) : ""
        const paidAdvance = `${totalApprovedAdvance ? totalApprovedAdvance.toFixed(2) : "-"} ${advanceCurrency}`

        const totalPaid = row?.totalApprovedDetails?.regular || 0
        const paidAffiliate = row.affiliatePaid || 0
        const paidRoyalty = `${(totalPaid - paidAffiliate).toFixed(2)} ${getCurrency(row.totalNetCurrency)}`
        const paidTotal = `${Number(row.totalApproved).toFixed(2)} ${getCurrency(row.totalNetCurrency)}`

        const totalApprovedPayment = row.totalApproved
        const remainingTotal = `${(Number(row.totalNetAll) - totalApprovedPayment).toFixed(2)} ${getCurrency(
          row.totalNetCurrency
        )}`

        const advanceTotal =
          row.advanceTotal > 0 ? `${Number(row.advanceTotal).toFixed(2)} ${getCurrency(row.advanceCurrency)}` : "-"

        const remaningAdvanceTotal =
          Number(row.advanceTotal) - Number(row.paidTotal) > 0 ? Number(row.advanceTotal) - Number(row.paidTotal) : 0

        const advanceRemaining =
          remaningAdvanceTotal > 0 ? `${remaningAdvanceTotal.toFixed(2)} ${getCurrency(row.advanceCurrency)}` : "-"

        const revenueTotalAllApproved = row.revenueTotalAllApproved || 0
        const paymentThreshHold = row.paymentThreshold || 0
        const percentage =
          paymentThreshHold > 0 ? ((revenueTotalAllApproved / paymentThreshHold) * 100).toFixed(2) : "100+"

        const revenueApproved = `${revenueTotalAllApproved.toFixed(1)} ${getCurrency(row.totalNetCurrency)}`
        const thresholdTotal = `${paymentThreshHold.toFixed(1)} ${getCurrency(row.totalNetCurrency)}`

        dataToDownload.push([
          row.userName,
          row.user,
          row.taxTypeName || "-",
          row.userCompany,
          row.paymentPeriodName || "-",
          revenueRoyalty,
          revenueAffiliate,
          revenueTotal,
          paidRoyalty,
          paidAffiliate.toFixed(2),
          paidAdvance,
          paidTotal,
          remainingTotal,
          advanceTotal,
          advanceRemaining,
          revenueApproved,
          thresholdTotal,
          `${percentage}%`
        ])
      })
    })
    downloadLink(dataToDownload)
  }

  const onClickExport = async () => {
    setDownloadLoading(true)
    summariesApi(`/export_summary_table/?type=${selectedFilter}${getFilterString(filters)}`, "get")
      .then((resp) => {
        setDownloadLoading(false)
        if (resp?.data?.["user_balance"]) {
          downloadUserBalance(resp.data["user_balance"])
        }
      })
      .catch(() => setDownloadLoading(false))
  }

  const currencyTooltipContent = (
    <div className="p-4 text-white">
      <KTitleSpan text={`Currency rates for ${selectedPeriod}`} fontSize={14} lineHeight="18px" color="#FFF" />
      <ul className={"pt-1"}>
        {currencyRates.map((currency) => (
          <li className={"mt-1 px-1 text-white"} key={currency.exchangeRates}>
            <KSpan
              text={`- ${currency.currencyTypeDetail.name} : ${currency.exchangeRates}`}
              fontSize={12}
              lineHeight="16px"
              color="#FFF"
            />
          </li>
        ))}
      </ul>
    </div>
  )

  const sumTooltipContent = (
    <div className="text-white p-4">
      <KTitleSpan text={`Totals for ${selectedPeriod}`} fontSize={14} lineHeight="18px" color="#FFF" />
      <ul className={"pt-1"}>
        <li className={"mt-1 px-1 text-white"}>
          <KSpan
            text={`- GROSS : ${sum(data.map((d) => parseFloat(String(d.totalAmount)))).toFixed(2)}`}
            fontSize={12}
            lineHeight="16px"
            color="#FFF"
          />
        </li>
        <li className={"mt-1 px-1 text-white"}>
          <KSpan
            text={`- NET : ${sum(data.map((d) => parseFloat(String(d.totalNet)))).toFixed(2)}`}
            fontSize={12}
            lineHeight="16px"
            color="#FFF"
          />
        </li>
      </ul>
    </div>
  )

  const approveReport = async () => {
    try {
      const response = await summariesApi(
        `/approve_status/?approve=True&type=all&user=${selectedUser?.value}&date=${selectedPeriod}`,
        "put",
        { id_list: selectedIds }
      )
      if (response.status === 200) {
        setApproveAllModalOpen(false)

        const summaries: AnalysisSummary[] = fetchedSummaries[selectedFilter]
        const updatedSummaries: AnalysisSummary[] = summaries.map((summary: AnalysisSummary) => {
          if (summary.id && selectedIds.includes(summary?.id)) {
            return { ...summary, approved: true }
          } else {
            return { ...summary }
          }
        })
        setSelectedIds([])
        let userHasAdvance = false

        if (
          selectedUser &&
          Array.isArray(fetchedSummaries["allAccounts"]) &&
          fetchedSummaries?.["allAccounts"][0]?.advanceTotal > 0
        ) {
          userHasAdvance = true
        }

        const toastContent = `${response.data}. ${
          userHasAdvance ? "There may be automatically approved payments. Please refresh the page to see it." : ""
        }`

        toast.success(toastContent, successFilledToastOptions)
        setFetchedSummaries({ ...fetchedSummaries, [selectedFilter]: updatedSummaries })
      }
    } catch {
      toast.error("Error report approve", errorFilledToastOptions)
    }
  }

  const approvePayment = async (
    paymentDay: string,
    date: string[],
    deductFromAdvance: boolean,
    approveAll: boolean,
    payWithRevolut: boolean = false,
    sendEmail: boolean = false,
    bankFee: string
  ) => {
    const isUserBalance = selectedFilter === ANALYSIS_FILTER.USER_BALANCE
    if (isUserBalance && !selectedPaymentUser) {
      return
    }

    if (!isUserBalance && !selectedUser?.value) {
      return
    }

    const requestBody: ReportsApprovePaymentRequest = {
      userId: isUserBalance ? selectedPaymentUser?.value || 0 : selectedUser?.value || 0,
      date,
      paymentDay,
      deductFromAdvance,
      bankFee: bankFee || "0",
      ...(payWithRevolut ? { payWithRevolut: true } : {}),
      ...(sendEmail ? { sendEmail: true } : {})
    }
    try {
      const userBalanceParam = isUserBalance && approveAll ? "&approve_all=1" : ""
      const selectedIdsParam = selectedIds?.length > 0 ? `&selected_ids=${selectedIds.join(",")}` : ""
      const response = await userPayments(
        `/approve_report_payment/?t=1${selectedIdsParam}${userBalanceParam}`,
        "post",
        mapToSnakeCase(requestBody)
      )
      if (response.status === 200) {
        if (!response.data.count) {
          if (response.data.message) {
            toast.error(`Error: ${response.data.message}`, errorFilledToastOptions)
            return
          }
          toast.error(`0 records found`, errorFilledToastOptions)
          return
        }
        toast.success(`Successfully updated ${response.data.count} records`, successFilledToastOptions)
        if (isUserBalance) {
          setLoading(true)
          fetchApprovalSummaries(selectedFilter, limit, offset, filters, sortField, sortDirection).then(() =>
            setLoading(false)
          )
        } else {
          const updatedSummaries = fetchedSummaries[selectedFilter].map((summary: AnalysisSummary) => {
            if (date.includes(summary.date) && summary?.approved) {
              if (selectedIds.length > 0) {
                if (selectedIds.includes(summary.id || 0)) {
                  return { ...summary, paymentApproved: true }
                } else {
                  return { ...summary }
                }
              } else {
                return { ...summary, paymentApproved: true }
              }
            } else {
              return { ...summary }
            }
          })
          setFetchedSummaries({ ...fetchedSummaries, [selectedFilter]: updatedSummaries })
        }
      } else {
        toast.error("Error payment approve", errorFilledToastOptions)
      }
    } catch (err) {
      toast.error("Error payment approve", errorFilledToastOptions)
    }
  }

  const onRefreshReports = () => {
    setLoading(true)
    fetchApprovalSummaries(selectedFilter, limit, offset, filters, sortField, sortDirection).then(() =>
      setLoading(false)
    )
  }

  const onFetchTotals = () => {
    setDownloadLoading(true)
    releases(`/summary/user_balance_totals/?t=1${getFilterString(filters)}`, "get")
      .then((resp) => {
        setDownloadLoading(false)
        if (resp.data.results) setTotals(mapListToCamelCase(resp.data.results))
      })
      .catch(() => {
        toast.error("Error fetching totals", errorFilledToastOptions)
        setDownloadLoading(false)
      })
  }

  const topRightFilters = (
    <div className="flex gap-2">
      {(selectedFilter === ANALYSIS_FILTER.ALL_PLATFORM_TOTAL || selectedFilter === ANALYSIS_FILTER.ALL_ACCOUNTS) && (
        <KDropdown
          width={70}
          menuWidth={80}
          options={currencyOptions}
          defaultValuePrimitive={selectedCurrency}
          selected={currencyOptions.find((option) => option.value2 === selectedCurrency)}
          onSelect={(selected: any) => setSelectedCurrency(selected?.value2)}
          rightIcon="/analytics_icons/caret-down-new.svg"
          enableIndicator
        />
      )}
      <KTooltip
        backgroundColor="#000"
        padding="0px 4px"
        arrowColor="#000"
        width="200px"
        position="bottom"
        align="left"
        marginLeft="24px"
        content={sumTooltipContent}
      >
        <div className="w-12 h-12 p-3.5 flex items-center justify-center rounded-[10px] bg-[#F7F7F7] ">
          <img src={WalletIcon} alt="wallet-icon" className="min-w-5 min-h-5" />
        </div>
      </KTooltip>

      {currencyRates.length > 0 && (
        <KTooltip
          backgroundColor="#000"
          padding="0px 4px"
          arrowColor="#000"
          width="200px"
          position="bottom"
          align="left"
          marginLeft="24px"
          content={currencyTooltipContent}
        >
          <KButton
            dataTestId="reports-approval-currency-button"
            icon={MoneyIcon}
            onClick={() => setCurrencyOfferModal(true)}
            width="48px"
            height="48px"
            disabled={
              !checkAdminPermission(ADMIN_PERMISSIONS.REPORTS_APPROVAL_UPDATE_CURRENCY, props.user?.adminPermissions)
            }
            padding="14px"
            background="#F7F7F7"
            hoverBackground="#F3F3F3"
            shadowDisabled
          />
        </KTooltip>
      )}
    </div>
  )

  const topFilters = (
    <div className="flex flex-row justify-between flex-grow">
      <div className="flex flex-row gap-2 items-center">
        <div className="mr-2">
          <KTooltip
            dataTestId="reports-approval-refresh-tooltip"
            position="bottom"
            content={<KSpan text="Refresh reports" />}
            children={<RefreshOutline onClick={() => onRefreshReports()} className="cursor-pointer" />}
          />
        </div>
        {selectedFilter === ANALYSIS_FILTER.USER_BALANCE && (
          <div className="flex gap-2">
            <div>
              <KDropdown
                placeholder="Threshol Visibility"
                width={100}
                menuWidth={140}
                options={thresholdVisibilityOptions}
                selected={selectedThresholdOption}
                onSelect={(selected) => {
                  if (!selected) {
                    return
                  }
                  setSelectedThresholdOption(selected as KSelectOption)
                }}
              />
            </div>
            <div>
              <KDropdown
                dataTestId="reports-approval-payment-period-filter"
                placeholder="P. Period (All)"
                width={140}
                menuWidth={170}
                isClearable={true}
                options={paymentPeriodFilterOptions}
                selected={selectedPaymentPeriodFilter}
                onSelect={(selected) => {
                  setSelectedPaymentPeriodFilter(selected as KSelectOption)
                }}
              />
            </div>
            <div>
              <KDropdown
                dataTestId="reports-approval-payment-status-filter"
                placeholder="Company"
                width={80}
                menuWidth={90}
                isClearable={true}
                options={companyOptions}
                selected={selectedCompany}
                onSelect={(selected) => {
                  setSelectedCompany(selected as KSelectOption)
                }}
              />
            </div>
            <div>
              <KDropdown
                dataTestId="reports-approval-payment-status-filter"
                placeholder="P. Status"
                width={95}
                menuWidth={120}
                isClearable={true}
                options={paymentStatusFilterOptions}
                selected={selectedPaymentStatusFilter}
                onSelect={(selected) => {
                  setSelectedPaymentStatusFilter(selected as KSelectOption)
                }}
              />
            </div>
            <div>
              <KDropdown
                dataTestId="reports-approval-payment-period-view"
                placeholder="Payment Period"
                width={125}
                menuWidth={170}
                options={paymentPeriodOptions}
                selected={selectedPaymentPeriod}
                onSelect={(selected) => {
                  if (!selected) {
                    return
                  }
                  setSelectedPaymentPeriod(selected as KSelectOption)
                }}
              />
            </div>
            {selectedFilter === ANALYSIS_FILTER.USER_BALANCE &&
              selectedPaymentPeriod.value === PaymentPeriods.Monthly && (
                <div>
                  <KDropdown
                    dataTestId="reports-approval-date-option-view"
                    placeholder="Date"
                    width={140}
                    menuWidth={170}
                    options={dateOptions}
                    selected={selectedDateOption}
                    onSelect={(selected) => {
                      if (!selected) {
                        return
                      }
                      setSelectedDateOption(selected as KSelectOption)
                    }}
                  />
                </div>
              )}
          </div>
        )}
        {selectedFilter === ANALYSIS_FILTER.USER_BALANCE &&
          selectedPaymentPeriod.value === PaymentPeriods.Quarterly && (
            <div>
              <KDropdown
                dataTestId="reports-approval-quarter-filter"
                width={120}
                options={quarters}
                selected={selectedQuarter}
                onSelect={(selected) => {
                  if (!selected) {
                    return
                  }
                  setSelectedQuarter(selected as KSelectOption)
                }}
              />
            </div>
          )}

        {((selectedFilter === ANALYSIS_FILTER.USER_BALANCE && selectedPaymentPeriod.value === PaymentPeriods.Monthly) ||
          (selectedFilter === ANALYSIS_FILTER.ALL_PLATFORM_TOTAL && kahunaPaymentOpen)) && (
          <div data-testid="reports-approval-monthly-filter" className="flex">
            <KSelectRange
              key={`filter-date-range`}
              value={
                selectedPeriodList.length > 0
                  ? [
                      yyyymmToDate(selectedPeriodList[0]),
                      yyyymmToDate(selectedPeriodList[selectedPeriodList.length - 1])
                    ]
                  : [yyyymmToDate(selectedPeriod), yyyymmToDate(selectedPeriod)]
              }
              onChange={(event) => {
                if (!event) {
                  setSelectedPeriodList([selectedPeriod])
                  return
                }
                setSelectedPeriodList(getMonthRange(event[0], event[1]))
              }}
              height="36px"
              width="auto"
              backgroundColor="#F7F7F7"
              hoverBackgroundColor="#F3F3F3"
              boxShadow="none"
              align={openSidebar ? "center" : "right"}
            />
            <div className="grid ml-1 mr-2 items-center">
              <KSpan text={`${selectedPeriodList[0]}`} />
              {selectedPeriodList.length > 1 && <KSpan text={`${selectedPeriodList[selectedPeriodList.length - 1]}`} />}
            </div>
          </div>
        )}

        {selectedPeriod &&
          (selectedFilter !== ANALYSIS_FILTER.USER_BALANCE ||
            (selectedFilter === ANALYSIS_FILTER.USER_BALANCE &&
              selectedPaymentPeriod.value === PaymentPeriods.Payment)) &&
          !(selectedFilter === ANALYSIS_FILTER.ALL_PLATFORM_TOTAL && kahunaPaymentOpen) && (
            <KSelectDate
              data-testid="reports-approval-select-date"
              key={selectedPeriodDate?.toString()}
              value={selectedPeriodDate}
              onChange={(selected: Date | undefined) => {
                if (!selected) return

                setSelectedPeriodDate(selected)
                const dateAsString = formatDateObject(selected) //YYYYMM
                setSelectedPeriod(dateAsString)
              }}
              onlyMonthSelection
              isTileDisabled={isTileDisabled}
              icon={CalendarIcon}
              height="36px"
              width="auto"
              backgroundColor="#F7F7F7"
              hoverBackgroundColor="#F3F3F3"
              anchorToButton
              buttonText={selectedPeriod}
              hideBody
              boxShadow="none"
              applyUndefinedValue={false}
              align={openSidebar ? "center" : "right"}
            />
          )}
        {!(selectedFilter === ANALYSIS_FILTER.ALL_PLATFORM_TOTAL && kahunaPaymentOpen) && (
          <SearchUserComponent className="relative" selectedUser={selectedUser} setSelectedUser={setSelectedUser} />
        )}

        {selectedFilter === ANALYSIS_FILTER.USER_BALANCE && (
          <div className="flex items-center gap-1 cursor-pointer" onClick={() => setApproved(!approved)}>
            <input checked={approved} type="checkbox" style={{ accentColor: "black" }} />
            <KSpan text="Approved" />
          </div>
        )}

        {/*selectedFilter === ANALYSIS_FILTER.USER_BALANCE && (
          <div className="flex items-center">
            <div>
              <KInput
                accentColor="black"
                type="checkbox"
                height={14}
                width={14}
                value={checkedThreshold ? "1" : "0"}
                shadowDisabled={true}
                checked={checkedThreshold}
                onChange={(value) => setCheckedThreshold(!checkedThreshold)}
              />
            </div>
            <KSpan text="Threshold Active" />
          </div>
        )*/}
        {selectedPeriod && selectedFilter === ANALYSIS_FILTER.ALL_PLATFORM_TOTAL && (
          <div className="ml-6">
            <KButton
              background="black"
              height="36px"
              textColor="white"
              text={!kahunaPaymentOpen ? "See Kahuna payments received" : "Default View"}
              onClick={() => setKahunaPaymentOpen(!kahunaPaymentOpen)}
            />
          </div>
        )}
      </div>
      <div className="flex flex-row gap-2">
        {selectedUser?.value &&
          userPaymentPeriodId &&
          selectedFilter === ANALYSIS_FILTER.TOP_PLATFORMS &&
          checkAdminPermission(ADMIN_PERMISSIONS.REPORTS_APPROVAL_PAYMENT_APPROVE, props.user?.adminPermissions) && (
            <KButton
              dataTestId="reports-payment-method-button"
              width="150px"
              padding="8px"
              height="36px"
              text={"Approve Payment"}
              onClick={() => setApprovePaymentModalOpen(true)}
              background="#F2FE67"
              hoverBackground="#F6FE8C"
              activeBackground="#F2FE67"
            />
          )}
        {selectedUser?.value &&
          selectedFilter === ANALYSIS_FILTER.TOP_PLATFORMS &&
          checkAdminPermission(ADMIN_PERMISSIONS.REPORTS_APPROVAL_REPORT_APPROVE, props.user?.adminPermissions) && (
            <KButton
              dataTestId="reports-approve-button"
              width="150px"
              padding="8px"
              height="36px"
              background="#000"
              hoverBackground="#111"
              activeBackground="#000"
              textColor="#FFF"
              text={"Approve Report"}
              onClick={() => setApproveAllModalOpen(true)}
              disabled={selectedIds.length === 0}
            />
          )}
        {selectedFilter === ANALYSIS_FILTER.USER_BALANCE && (
          <KButton
            dataTestId="reports-totals-button"
            gap="8px"
            height="36px"
            background="transparent"
            hoverBackground="#11111140"
            disabled={downloadLoading}
            leftIcon={PresentationIcon}
            text={"Totals"}
            onClick={() => onFetchTotals()}
          />
        )}
        {selectedFilter === ANALYSIS_FILTER.USER_BALANCE && (
          <KButton
            dataTestId="reports-export-button"
            gap="8px"
            height="36px"
            background="black"
            hoverBackground="#11111140"
            textColor="white"
            disabled={
              downloadLoading ||
              !checkAdminPermission(ADMIN_PERMISSIONS.REPORTS_APPROVAL_EXPORT, props.user?.adminPermissions)
            }
            leftIcon={DownloadIcon}
            text={"Export"}
            onClick={() => onClickExport()}
          />
        )}
      </div>
    </div>
  )

  const tableTitle = (
    <div className="flex flex-row gap-2 items-center !cursor-default">
      <KTooltip
        backgroundColor="#000"
        padding="0px 4px"
        height="20px"
        arrowColor="#000"
        position="bottom"
        content={
          <div className="h-5 w-max flex items-center">
            <KSpan text={openSidebar ? "Hide sidebar" : "Open sidebar"} fontSize={12} lineHeight="16px" color="#FFF" />
          </div>
        }
      >
        <div className="flex items-center">
          <KButton
            dataTestId="reports-sidebar-toggle-button"
            icon="/left-sidebar.svg"
            width="32px"
            height="32px"
            background="transparent"
            shadowDisabled={true}
            padding="6px"
            onClick={() => {
              if (setOpenSidebar) {
                setOpenSidebar(!openSidebar)
              }
            }}
          />
        </div>
      </KTooltip>
      <div>
        {" "}
        <KTitleSpan text="Reports Approval" color="#111" fontSize={20} lineHeight="28px" />
      </div>
    </div>
  )

  const renderAnalysisFilterOptions = () => {
    const options =
      props.user?.roleId === USER_ROLE.AccountingAdmin
        ? [
            {
              value: 6,
              value2: ANALYSIS_FILTER.USER_BALANCE,
              label: "User Balance",
              icon: MoneyIcon
            }
          ]
        : analysisFilterOptions

    return options.map((analysisFilterOption, index: number) => {
      const isSelected = analysisFilterOption.value2 === selectedFilter
      return (
        <div
          data-testid={`reports-approval-${analysisFilterOption.value2}`}
          className="py-3.5 px-4 h-11"
          key={`${index}`}
        >
          <div
            className={`rounded-[10px] p-2 flex flex-row gap-2 ${
              isSelected ? "bg-[#FFF]" : "transparent"
            } cursor-pointer whitespace-nowrap`}
            onClick={() => {
              if (analysisFilterOption?.value2) {
                setSelectedFilter(analysisFilterOption?.value2)
                if (analysisFilterOption?.value2 === ANALYSIS_FILTER.USER_BALANCE) {
                  setSelectedThresholdOption(thresholdVisibilityOptions[2])
                } else {
                  setSelectedThresholdOption(thresholdVisibilityOptions[0])
                }
              }
            }}
          >
            <img src={analysisFilterOption.icon} width={16} height={16} className="shrink-0" />
            <KSpan text={analysisFilterOption.label} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
          </div>
        </div>
      )
    })
  }

  const getActivityPeriodFromPaymentPeriod = () => {
    if (selectedFilter === ANALYSIS_FILTER.USER_BALANCE && selectedPaymentPeriod.value === PaymentPeriods.Payment) {
      const year = Number(selectedPeriod.slice(0, 4))
      const month = Number(selectedPeriod.slice(4, 6)) - 1 // JS months are 0-based

      const date = new Date(year, month)
      date.setMonth(date.getMonth() - 3) // User Balance & Payment Period is selected as Payment View

      const newYear = date.getFullYear()
      const newMonth = String(date.getMonth() + 1).padStart(2, "0")

      return `${newYear}${newMonth}`
    }

    return selectedPeriod
  }

  const renderData = () => {
    if (selectedThresholdOption.value === 1) {
      return [...data]
    } else if (selectedThresholdOption.value === 2) {
      return [...data.filter((entity) => entity.revenueTotalAllApproved < entity.paymentThreshold)]
    } else if (selectedThresholdOption.value === 3) {
      return [...data.filter((entity) => entity.revenueTotalAllApproved >= entity.paymentThreshold)]
    }
    return data
  }

  return (
    <div className={"catalog flex bg-[#fff]"}>
      <div
        className={`${
          openSidebar ? "min-w-[230px]" : "min-w-0 !w-[0px]"
        } max-w-[320px] w-[20%] bg-[#F7F7F7] min-h-[100vh] flex flex-col overflow-hidden shrink-0`}
        style={{
          transitionDuration: "0.5s"
        }}
      >
        <div className="pl-6 pr-3 py-4 whitespace-nowrap">
          <KTitleSpan text={"Reports Approval"} lineHeight="28px" fontSize={20} color="#111" />
        </div>
        {selectedFilter && renderAnalysisFilterOptions()}
      </div>
      <div className="grow flex justify-center">
        <div className="w-full">
          {totals.length > 0 && (
            <div className="flex justify-end">
              <div>
                <div className="flex mb-2">
                  <div style={{ width: "100px" }}>
                    <KSpan text="Currency" />
                  </div>
                  <div style={{ width: "120px" }}>
                    <KSpan text="Revenue Total" />
                  </div>
                  <div style={{ width: "120px" }}>
                    <KSpan text="Paid Total" />
                  </div>
                  <div style={{ width: "120px" }}>
                    <KSpan text="Remaining Total" />
                  </div>
                </div>
                {totals.map((userBalanceTotal) => {
                  return (
                    <div className="flex">
                      <div data-testid="user-balance-total-currency" style={{ width: "100px" }}>
                        <KSpan text={userBalanceTotal.currency} />
                      </div>
                      <div style={{ width: "120px" }}>
                        <KSpan
                          text={
                            userBalanceTotal.totalNet?.toLocaleString("en-EN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            }) || "-"
                          }
                        />
                      </div>
                      <div style={{ width: "120px" }}>
                        <KSpan
                          text={
                            userBalanceTotal.totalApproved?.toLocaleString("en-EN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            }) || "-"
                          }
                        />
                      </div>
                      <div style={{ width: "120px" }}>
                        <KSpan
                          text={
                            isNaN(userBalanceTotal.totalNet)
                              ? "___________"
                              : (userBalanceTotal.totalNet - userBalanceTotal.totalApproved)?.toLocaleString("en-EN", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                }) || "-"
                          }
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
          {selectedFilter === ANALYSIS_FILTER.PAYMENT_BATCHES ? (
            <PaymentBatches />
          ) : (
            <TableNew
              title={tableTitle}
              topRightElement={topRightFilters}
              topFilters={topFilters}
              columns={columns}
              data={renderData()}
              paginationValidation={false}
              loading={loading}
              pagination
              limit={limit}
              freezeHeader
              onSortOrFilterChange={onSortOrFilterChange}
              count={fetchedSummaries.count}
              overrideTableOverflow={"scroll"}
              onClickPage={onClickPage}
              additionalFilters={{
                date: selectedPeriod,
                date_list: selectedPeriodList,
                admin_currency: selectedCurrency,
                quarter: selectedQuarter?.value2,
                selected_payment_period: selectedPaymentPeriod?.value,
                selected_payment_period_filter: selectedPaymentPeriodFilter?.value,
                selected_payment_status_filter: selectedPaymentStatusFilter?.value2,
                selected_company: selectedCompany?.value2,
                selected_date_option: selectedDateOption.value,
                kahuna_payment_open: kahunaPaymentOpen ? "1" : "0",
                selectedFilter,
                offset,
                approved,
                checked_threshold: checkedThreshold,
                ...(selectedUser?.value && { user__username: selectedUser.label })
              }}
              onSelectRow={onSelectRow}
              notFoundTitle={"No Reports"}
              notFoundDesc={"No reports found matching the applied filters."}
            />
          )}
        </div>
      </div>

      {editedSummary && (
        <ApproveModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          summary={editedSummary}
          disapproveSummary={disapproveReportSummary}
          approveSummary={approveReportSummary}
          transferSummary={transferReportSummary}
          transferBackSummary={transferBackReportSummary}
        />
      )}
      <ApproveAllModal
        open={approveAllModalOpen}
        onClose={() => setApproveAllModalOpen(false)}
        approve={approveReport}
        description={
          selectedIds.length === (fetchedSummaries[selectedFilter] || []).length
            ? "Are you sure you want to approve all reports on the table?"
            : "Are you sure you want to approve the selected reports?"
        }
      />
      <ApprovePaymentModal
        selectedIds={selectedIds}
        selectedPeriod={getActivityPeriodFromPaymentPeriod()}
        userPaymentPeriodId={userPaymentPeriodId}
        selectedUsername={selectedUser?.label}
        selectedUserId={selectedUser?.value}
        open={approvePaymentModalOpen}
        onClose={() => setApprovePaymentModalOpen(false)}
        approve={approvePayment}
        selectedFilter={selectedFilter}
        selectedPaymentUser={selectedPaymentUser}
      />
      <CurrencyExchangeModal open={currencyOfferModal} onClose={() => setCurrencyOfferModal(false)} />
      <AdminPaymentMethodModal
        open={selectedPaymentMethodUserId !== undefined}
        onClose={() => setSelectedPaymentMethodUserId(undefined)}
        paymentMethodsAdminEntry={undefined}
        userId={selectedPaymentMethodUserId}
        readOnly={true}
        isModal={true}
      />
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    summaries: state.admin.summaries,
    user: state.auth.user
  }
}

export default connect(mapStateToProps, {
  fetchApprovalSummaries,
  approveSummary,
  disapproveSummary,
  transferSummary,
  updateApprovalSummaries,
  transferBackSummary,
  paymentMethodsAdminEntry,
  selectUser
})(ReportsApproval)
